/**
 * Canonical Alert detail, the immutable activity timeline, and workflow writes.
 *
 * The three reads are deliberately separate from the Queue/History list: a list
 * row is a summary projection, and treating it as the detail object is how a
 * stale status ends up driving an operator's decision.
 */
import type { AxiosResponse } from "axios";
import axiosClient from "@/shared/lib/axios/axios.ts";
import { API_ROUTES } from "@/alerts/api/config.ts";
import {
  unwrapEnvelope,
  toAlertApiError,
  type ApiEnvelope,
} from "@/alerts/api/envelope.ts";
import type {
  AlertActionRequest,
  AlertActionResult,
  AlertActivity,
  AlertWorkspaceDetail,
} from "@/alerts/types/alerts-workspace.ts";

/** GET /api/alerts/{id}/ — the authoritative Alert, evidence included. */
export const getAlertWorkspaceDetail = async (
  id: number,
  signal?: AbortSignal,
): Promise<AlertWorkspaceDetail> => {
  try {
    const response = await axiosClient.get<
      unknown,
      AxiosResponse<ApiEnvelope<AlertWorkspaceDetail, unknown>>
    >(`${API_ROUTES.ALERTS}${id}/`, { signal });
    return unwrapEnvelope(response).data;
  } catch (error) {
    throw toAlertApiError(error);
  }
};

/**
 * GET /api/alerts/{id}/activities/ — immutable workflow history.
 *
 * Sorted newest-first here rather than at the render site: the order is part of
 * how the timeline reads, and every consumer wants the same one.
 */
export const getAlertActivities = async (
  id: number,
  signal?: AbortSignal,
): Promise<AlertActivity[]> => {
  try {
    const response = await axiosClient.get<
      unknown,
      AxiosResponse<ApiEnvelope<AlertActivity[], unknown>>
    >(`${API_ROUTES.ALERTS}${id}/activities/`, { signal });
    const rows = unwrapEnvelope(response).data ?? [];
    return [...rows].sort((a, b) => b.seq - a.seq);
  } catch (error) {
    throw toAlertApiError(error);
  }
};

/**
 * Headers for a workflow write.
 *
 * - `Idempotency-Key` identifies the user's INTENT. A network retry of an
 *   already-submitted intent must reuse it, or the action applies twice; a new
 *   intent must never reuse one, or the server replays the old result.
 * - `If-Match` carries the workflow version the operator was looking at, quoted
 *   per the contract. Without it, two operators reviewing the same alert
 *   silently overwrite each other. `add_note` may omit it — a note changes no
 *   workflow state, so there is nothing to conflict with.
 */
export const buildAlertActionHeaders = (
  idempotencyKey: string,
  workflowVersion?: number | null,
): Record<string, string> => {
  const headers: Record<string, string> = { "Idempotency-Key": idempotencyKey };
  if (workflowVersion != null) headers["If-Match"] = `"${workflowVersion}"`;
  return headers;
};

/**
 * Serialize a command into its request body.
 *
 * Only the fields belonging to the selected command are emitted. `reason_code`
 * belongs to `reopen` and to nothing else; `related_alert_id` is forbidden
 * outright; and a stray `note: ""` would read as an empty comment rather than as
 * no comment.
 */
export const buildAlertActionBody = (action: AlertActionRequest): Record<string, unknown> => {
  const note = "note" in action ? action.note?.trim() : undefined;
  const body: Record<string, unknown> = { command: action.command };
  if (action.command === "close_alert") body.disposition = action.disposition;
  if (action.command === "correct_result") {
    // Both are required by the contract for a correction; omitting them is why
    // "Correct" could not actually flip a completed decision (MIN-270).
    body.target_result = action.target_result;
    if (action.target_result === "closed") body.disposition = action.disposition;
  }
  if (action.command === "reopen") body.reason_code = action.reason_code;
  if (note) body.note = note;
  return body;
};

/** POST /api/alerts/{id}/actions/ — the only workflow write in the app. */
export const postAlertAction = async (
  id: number,
  action: AlertActionRequest,
  options: { idempotencyKey: string; workflowVersion?: number | null },
): Promise<AlertActionResult> => {
  try {
    const response = await axiosClient.post<
      unknown,
      AxiosResponse<ApiEnvelope<AlertActionResult, unknown>>
    >(`${API_ROUTES.ALERTS}${id}/actions/`, buildAlertActionBody(action), {
      headers: buildAlertActionHeaders(
        options.idempotencyKey,
        // A note carries no version — see buildAlertActionHeaders.
        action.command === "add_note" ? null : options.workflowVersion,
      ),
    });
    return unwrapEnvelope(response).data;
  } catch (error) {
    throw toAlertApiError(error);
  }
};
